/** A resident's current public AOI facts. No copied actor snapshot can outlive visibility. */
import { S, esc } from '../../core/state.js';
import { isResident, muted, sec } from './common.js';
import { levelLabel } from '../../core/level-label.js';
import { alignmentSummary } from '../alignment.js';
import { levelGrowthSummary } from '../level-growth.js';
import { nativeActionAllowed } from '../../core/net.js';
import { ZONE_KO } from '../../world/map.js';
import { josa } from '../../world/josa.js';

function selected() { return S.players.find(p=>p.id===S.residentId && isResident(p)); }
function giftChoices() {
  const inv=S.you?.inv||{},cons=inv.consum||{},junk=inv.junk||[];
  return [
    ['pot_s','작은 물약',Number(cons.pot_s)||0],
    ['pot_m','중급 물약',Number(cons.pot_m)||0],
    ['shard','강화석',Number(junk.shard)||0],
  ].filter(([, ,n])=>n>0);
}
/* ───── R229 — 나와의 관계: 친밀도·만남·공동 처치·저장된 최근 사건 · 「인사하기」 ───── */
/** 서버가 준 사건 종류 → 한 줄. 서버가 기억한 것만 표시하며 모르는 종류의 이야기는 지어내지 않는다. */
const RECENT_TEXT = {
  foughtTogether: (r) => r.monster ? `함께 ${r.monster}${josa(r.monster, '을')} 잡았다` : '함께 몬스터를 잡았다',
  traded: () => '물품을 교환했다', gifted: () => '선물을 주고받았다', introduced: () => '소개로 서로 알게 됐다',
  helped: () => '서로 도왔다', worldDefended: () => '함께 세계를 지켰다', betrayed: () => '원주민을 해쳐 믿음을 잃었다',
  returned: (r) => `${ZONE_KO[r.zone] || '사냥터'}에서 쓰러졌다가 돌아왔다`,
  fell: (r) => `${ZONE_KO[r.zone] || '사냥터'}에서 쓰러졌다`,
  met: () => '인사를 나눴다',
};
function ago(at) {
  const s = Math.max(0, Math.floor(Date.now() / 1000 - Number(at || 0)));
  return s < 60 ? '방금' : s < 3600 ? `${Math.floor(s / 60)}분 전` : s < 86400 ? `${Math.floor(s / 3600)}시간 전` : `${Math.floor(s / 86400)}일 전`;
}
/** 다시 인사할 수 있을 때까지 남은 초(받은 순간의 서버 값에서 흐른 시간을 뺀다) */
function greetLeft(rel) {
  return Math.max(0, Math.ceil(Number(rel?.greetWait || 0) - (Date.now() - Number(rel?.receivedAt || 0)) / 1000));
}
function relationSection(p) {
  const rel = S.residentRel?.id === p.id ? S.residentRel : null;
  if (!rel) {
    const asked = S.residentRelAsked?.id === p.id ? S.residentRelAsked : null;
    return sec('나와의 관계', asked && Date.now() - asked.at > 8000
      ? muted('관계를 불러오지 못했다.') + '<button onclick="act.residentRelRetry()">다시 불러오기</button>'
      : muted('관계를 불러오는 중이다.'));
  }
  const hearts = Math.max(0, Math.min(5, Math.floor(Number(rel.hearts) || 0)));
  const events = (Array.isArray(rel.recentEvents) ? rel.recentEvents : rel.recent ? [rel.recent] : []).slice(0, 5);
  const memories = events.length ? '<details class="ux-details" id="resident-memories"><summary>함께 겪은 일 · ' + events.length + '건</summary><ol style="padding-left:1.3em;overflow-wrap:anywhere">'
      + events.map((event) => `<li>${esc((RECENT_TEXT[event.kind] || (() => '최근 사건이 기록됐다'))(event))} <span class="qt">· ${event.approximate ? '약 ' : ''}${ago(event.at)}</span></li>`).join('')
      + '</ol></details>' : '';
  let body = `<div class="kv"><span>${rel.friend ? '친구' : '나와의 친분'}</span><b class="relhearts" aria-label="친밀도 ${Number(rel.score)}점 · 100점 만점">${'♥'.repeat(hearts)}${'♡'.repeat(5 - hearts)} <span class="ux-number">${Number(rel.score)}/100</span></b></div>`;
  if (!rel.socialOn) body += muted('이 세계에서는 원주민이 대화하지 않는다 — 「인사하기」에 짧게 답할 뿐 친밀도는 쌓이지 않는다.');
  else if (rel.native) body += muted('사냥터에서는 인사에 답만 한다 — 친밀도는 마을에서 가까이 만나 인사하거나 말을 걸 때 오른다.');
  else if (!rel.friend) body += muted(`친밀도 ${Number(rel.friendAt)}점이면 친구가 된다 — 가까이서 인사하거나 말을 걸면 오른다.`);
  else body += muted('친구다 — 마을에서 가까이 만나면 교환·선물을 받아 준다.');
  const left = greetLeft(rel);
  body += !rel.greetOn ? '<button disabled>인사하기 — 지금은 쓸 수 없다</button>'
    : p.d ? '<button disabled>인사하기 — 쓰러져 있다</button>'
    : left > 0 ? `<button disabled>인사하기 — ${left}초 뒤</button>`
    : '<button onclick="act.residentGreet()">인사하기</button>';
  return sec('나와의 관계', body + `<p class="ux-note">${Number(rel.meetings)}번 만남 · 함께 잡은 몬스터 ${Number(rel.together)}마리</p>` + memories);
}

export function stateKey() {
  const p=selected();
  const expired=!!S.nativeTrade?.offer&&Date.now()/1000>S.nativeTrade.offer.expires;
  // R229 — 관계 답·인사 대기 초·못 받은 요청(8초 뒤 [다시 불러오기])도 다시 그리는 열쇠다
  const rel=p&&S.residentRel?.id===p.id?S.residentRel:null, relLate=!rel&&!!S.residentRelAsked&&Date.now()-S.residentRelAsked.at>8000;
  // 사냥터↔마을을 건너면 교환 칸이 켜지고 꺼진다(R206) — 그 사실도 다시 그리는 열쇠에 넣는다
  return JSON.stringify(p ? [p.id,p.n,p.k,p.lv,p.hp,p.d,p.rank,p.crime,p.alignment,p.levelGrowth,p.resident,S.nativeTrade,expired,nativeActionAllowed('nativeTrade'),rel,rel?greetLeft(rel):0,relLate] : [S.residentId,null]);
}
export function render() {
  const p=selected();
  if(!p) return sec('원주민이 시야를 벗어났다',muted('다시 가까이에서 원주민을 누르면 현재 모습을 살펴볼 수 있다.'));
  const r=p.resident;
  let h='<section class="ux-hero"><p class="ux-kicker">이 세계에서 살아가는 원주민</p><h3 class="ux-title">' + esc(p.n) + '</h3>'
    + `<p class="ux-summary">${p.d?'지금 쓰러져 있다':`체력 ${Number(p.hp)}%`}${r?.organization ? ' · 〈' + esc(r.guild?.name || r.organization) + '〉' : ''}</p></section>`;
  h += relationSection(p);
  let profile = `<div class="kv"><span>성장 · 직업</span><b>Lv.${levelLabel(p.lv)} · ${p.k==='ranged'?'원거리':p.k==='assassin'?'암살자':'근접'}</b></div>`
    + alignmentSummary(p.alignment, false) + levelGrowthSummary(p.levelGrowth);
  if(!r) profile+=sec('소속 길드',muted('소속 정보를 받는 중이다. 잠시 뒤 다시 보인다.'));
  else if(r.organization) {
    const guildName = r.guild?.name || r.organization;
    profile+=sec('소속 길드',`<div style="overflow-wrap:anywhere"><h3>${esc(guildName)}</h3>`
      + `<div class="kv"><span>길드에서 맡은 자리</span><b>${r.leader?'길드장':'길드원'}</b></div>`
      + `<div class="kv"><span>소속 원주민</span><b>${Number(r.members)}명</b></div>`
      + (r.guild?.symbol?muted(`${r.guild.symbol} 게임 길드 표식 · ${r.guild.role==='master'?'길드장':'길드원'}`):'')
      + (r.active?muted('원주민들이 결성한 길드다.'):muted('현재 길드 활동은 쉬고 있다. 소속은 그대로 남아 있다.'))+'</div>');
  } else profile+=sec('소속 길드',muted('아직 소속된 원주민 게임 길드가 없다.'));
  if(p.crime||p.rank) profile+=sec('이름 곁의 표식',
    (p.crime?muted(`💀 범죄 ${Number(p.crime)}단계`):'')
    +(p.rank?muted(`전투력 순위 ${Number(p.rank)}위 · 길드 직위와는 관계없다.`):''));
  const t=S.nativeTrade?.npcId===p.id?S.nativeTrade:null,o=t?.offer;
  const pending=t?.status==='pending'||t?.status==='loading';
  /**
   * R206(레드팀 D2-23) — 사냥터(러스트) 소켓은 원주민 교환(`nativeTrade`)을 받지 않는다. 전에는 버튼을 그대로 그려,
   * 누르면 「확인하고 있다」로 바뀐 채 응답이 영영 없어 버튼이 사라졌다(마을로 돌아갈 때까지). 사냥터에서는 버튼을
   * 끄고 이유(「마을에서 가능」)를 버튼에 적는다.
   */
  if(!nativeActionAllowed('nativeTrade')) {
    const off=muted('교환·선물은 마을에서 가까이 만나야 된다')
      +'<button disabled>새 거래 제안 살펴보기 — 마을에서 가능</button>';
    h+=sec('필요한 물품 교환',off);
    return h + '<details class="ux-details" id="resident-profile"><summary>성장·성향·소속 살펴보기</summary>' + profile + '</details>';
  }
  let trade=muted('물약·강화석을 은화로 교환 · 친분이 쌓이면 선물도 된다');
  if(t?.needs)trade+=muted(`지금 필요한 물품: 물약 ${Number(t.needs.potions)}개 · 강화석 ${Number(t.needs.shards)}개`);
  if(t?.message)trade+=muted(esc(t.message));
  if(t?.status==='offered'&&o){
    const expired=Date.now()/1000>o.expires;
    trade+=`<div class="kv"><span>${o.gift?'선물하는':`내가 ${o.npcBuys?'건네는':'받는'}`} 물품</span><b>${esc(o.item)} ${Number(o.quantity).toLocaleString()}개</b></div>`
      +(o.gift?muted('은화 없이 건네며, 원주민이 수락하면 친분이 쌓이고 물품을 전달한다.'):`<div class="kv"><span>내가 ${o.npcBuys?'받는':'내는'} 은화</span><b>${Number(o.total).toLocaleString()}</b></div>`)
      +muted(expired?'제안 시간이 끝났다. 새 거래 제안을 다시 살펴봐 달라.':'제안 유효 시간 ' + new Date(Number(o.expires) * 1000).toLocaleTimeString('ko-KR') + '까지 · 수락할 때 거리·재고·생활비·가방 무게를 다시 확인한다.')
      +`<button ${expired?'disabled':''} onclick="act.nativeTradeAccept()">이 조건으로 교환 수락</button> <button onclick="act.nativeTradeCancel()">취소</button>`;
  }
  if(!pending) {
    trade+='<button onclick="act.nativeTradeQuote()">새 거래 제안 살펴보기</button>';
    const choices=giftChoices();
    if(choices.length) trade+=`<div class="row" style="margin-top:6px"><select id="nativegiftitem">${choices.map(([id,name,n])=>`<option value="${id}">${name} · ${n.toLocaleString()}개</option>`).join('')}</select><input id="nativegiftqty" type="number" min="1" max="${Math.max(...choices.map(([, ,n])=>n))}" value="1" inputmode="numeric"><button onclick="act.nativeGiftQuote()">선물 제안</button></div>`;
    else trade+=muted('선물할 물품이 가방에 없다.');
  }
  else trade+=muted(t.status==='loading'?'현재 필요한 물품을 확인하고 있다.':'교환 결과를 확인 중이다. 접속이 끊겨도 물품과 은화는 중복 차감되지 않는다.');
  h+=sec('필요한 물품 교환',trade);
  return h + '<details class="ux-details" id="resident-profile"><summary>성장·성향·소속 살펴보기</summary>' + profile + '</details>' + muted('표시되는 정보는 지금 시야에 있는 원주민의 상태를 따라 바뀐다.');
}
